<i18n>
en:
  title: "User information"
  stepper:
    basic: "Basic information"
    terms: "Terms"
  mail:
    label: "E-mail Address (read only)"
  password:
    label:
      new: "New password"
      edit: "New password (only when changing)"
    hint: "8 or more alphanumeric symbols"
    confirm:
      label: "Re-enter password"
      error: "Passwords do not match"
  name:
    label: "Name"
  address:
    label: "Address"
  tel:
    label: "Phone"
    hint: "Only numbers without hyphens"
  sex:
    label: "Gender"
    man: "man"
    female: "female"
    other: "other"
  birthday:
    year:
      label: "Birthday - Year"
    month:
      label: "Month"
    day:
      label: "Day"
  tos:
    title: "Terms of Service"
    label: "I agree that Terms of Service"

  back: "Back"
  next: "Next"
  submit: "Regist"
  error:
    IUM002: "Unable to register due to expiration.<br>Please start over."
    IUM003: "URL is incorrect.<br>Please start over."

ja:
  title: "ユーザー情報"
  stepper:
    basic: "基本情報"
    terms: "規約"
  mail:
    label: "メールアドレス（変更不可）"
  password:
    label:
      new: "パスワード"
      edit: "パスワード（変更時のみ入力）"
    hint: "半角英数字を組み合わせた8桁以上で入力してください"
    confirm:
      label: "パスワード（確認）"
      error: "パスワードが一致しません"
  name:
    label: "氏名"
  address:
    label: "住所"
  tel:
    label: "電話番号"
    hint: "ハイフンなしの数字のみで入力してください"
  sex:
    label: "性別"
    man: "男性"
    female: "女性"
    other: "その他"
  birthday:
    year:
      label: "誕生日 - 年"
    month:
      label: "月"
    day:
      label: "日"
  tos:
    title: "利用規約"
    label: "利用規約に同意します"

  back: "戻る"
  next: "次へ"
  submit: "登録する"
  error:
    IUM002: "有効期限切れのためユーザー登録できません。<br>最初からやり直してください。"
    IUM003: "URLが正しくありません。<br>最初からやり直してください。"
</i18n>

<template lang="pug">
  div.my-6
    v-container
      //- タイトル
      v-row(v-if="!newEntry")
        v-col(cols="12" align="center")
          h2|{{$t('title')}}
        v-col.pt-0.pb-4(cols="12" align="center")
          v-img(src="@/assets/images/title-underline.png" width="227px" height="24px")

      //- 読込中
      template(v-if="isLoading")
        v-row
          v-col.my-12.pa-12(cols="12")
            v-progress-linear(indeterminate)

      //- 認証失敗
      template(v-else-if="!certify")
        v-row
          v-col.my-12.py-12(cols="12" align="center")
            v-alert.d-inline-block(dense outlined type="error")
              div(v-html="errMsg")

      //- 入力フォーム
      template(v-else)
        v-stepper.transparent.none-shadow(alt-labels v-model="step")
          v-stepper-header.px-md-12.px-lg-12.px-xl-12.none-shadow
            v-stepper-step(:step="1" :complete="step > 1")|{{$t('stepper.basic')}}
            v-divider
            v-stepper-step(:step="2" :complete="step > 2")|{{$t('stepper.terms')}}

          v-stepper-items
            //- STEP1
            v-stepper-content.px-0(:step="1")
              v-form.px-md-8.px-lg-10.px-xl-12(onsubmit="return false" v-model="valid.step1")
                v-row
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-text-field(prepend-icon="fa-envelope" :label="$t('mail.label')" maxlength="255" v-model="forms.mail" readonly)

                v-row(v-if="newEntry")
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-text-field.required(type="password" prepend-icon="fa-key" :label="$t('password.label.new')" persistent-hint :hint="$t('password.hint')" :rules="v($t('password.label.new'), ['required', 'password'])" maxlength="32" v-model="forms.password")
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-text-field.ml-8.ml-md-0.required(type="password" :label="$t('password.confirm.label')" :rules="v($t('password.confirm.label'), ['required'])" :error="validPassword.error" :error-messages="validPassword.message" maxlength="32" v-model="forms.passwordConfirm")
                v-row(v-else)
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-text-field(type="password" prepend-icon="fa-key" :label="$t('password.label.edit')" persistent-hint :hint="$t('password.hint')" :rules="v($t('password.label.edit'), ['password'])" maxlength="32" v-model="forms.password")
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-text-field.ml-8.ml-md-0(type="password" :label="$t('password.confirm.label')" :error="validPassword.error" :error-messages="validPassword.message" maxlength="32" v-model="forms.passwordConfirm")

                v-row
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-text-field.required(prepend-icon="fa-signature" :label="$t('name.label')" :rules="v($t('name.label'), ['required'])" maxlength="64" v-model="forms.name")

                v-row
                  v-col
                    v-text-field.required(prepend-icon="fa-map-marked" :label="$t('address.label')" :rules="v($t('address.label'), ['required'])" maxlength="128" v-model="forms.address")

                v-row
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-text-field.required(type="tel" prepend-icon="fa-phone" :label="$t('tel.label')" persistent-hint :hint="$t('tel.hint')" :rules="v($t('tel.label'), ['required', 'number'])" maxlength="24" v-model="forms.tel")

                v-row
                  v-col(cols="12" sm="10" md="6" xl="4")
                    v-select.required(prepend-icon="fa-venus-mars" :label="$t('sex.label')" :items="sexes" :rules="v($t('address.label'), ['required'])" v-model="forms.sex")

                v-row
                  v-col(cols="12" sm="4" md="3" xl="2")
                    v-text-field.required(type="tel" prepend-icon="fa-birthday-cake" :label="$t('birthday.year.label')" :rules="v($t('birthday.year.label'), ['required', 'number'])" maxlength="4" v-model.number="forms.birthdayYear")
                  v-col(cols="12" sm="3" md="2" xl="1")
                    v-select.ml-8.ml-sm-0.required(:label="$t('birthday.month.label')" :items="months" :rules="v($t('birthday.month.label'), ['required'])" v-model.number="forms.birthdayMonth")
                  v-col(cols="12" sm="3" md="2" xl="1")
                    v-select.ml-8.ml-sm-0.required(:label="$t('birthday.day.label')" :items="birthdayDays" :rules="v($t('birthday.day.label'), ['required'])" v-model.number="forms.birthdayDay")

                //- 次へ
                v-row.mt-12(justify="center")
                  v-col(cols="12" sm="4" md="3" xl="2")
                    v-btn(x-large block type="submit" color="primary" @click="nextStep(1)" :disabled="!valid.step1")|{{$t('next')}}

            //- STEP2
            v-stepper-content.px-0(:step="2")
              v-form.px-md-8.px-lg-10.px-xl-12(onsubmit="return false" v-model="valid.step2")
                v-row
                  v-col(cols="12" align="center")
                    section
                      h1|{{$t('tos.title')}}
                      div.tos-box
                        TermsOfService

                      v-checkbox.d-inline-block(color="success" :label="$t('tos.label')" :rules="v($t('tos.title'), ['requiredBool'])" v-model="forms.agree")

                //- 次へ
                v-row.mt-12(justify="center")
                  v-col(cols="12" sm="4" md="3" xl="2")
                    v-btn(x-large block color="secondary" @click="nextStep(-1)")|{{$t('back')}}
                  v-col(cols="12" sm="4" md="3" xl="2")
                    v-btn(x-large block type="submit" color="primary" @click="regist" :loading="isSubmit" :disabled="!valid.step2")|{{$t('submit')}}

                //- エラーメッセージ
                v-row(v-if="errMsg")
                  v-col(cols="12" align="center")
                    v-alert.d-inline-block(dense outlined type="error")
                      div(v-html="errMsg")
</template>

<script>
import TermsOfService from '@/components/documents/TermsOfService'

export default {
  components: {
    TermsOfService
  },
  data () {
    return {
      // 新規登録（false: 編集）
      newEntry: true,
      // 入力フォーム
      forms: {
        mail: null,
        password: null,
        passwordConfirm: null,
        name: null,
        address: null,
        tel: null,
        sex: null,
        birthdayYear: null,
        birthdayMonth: null,
        birthdayDay: null,
        agree: false
      },
      // 入力バリデーション
      valid: {
        step1: false,
        step2: false
      },
      // ステップ
      step: 1,
      // 送信中
      isSubmit: false,
      // 読込中
      isLoading: true,
      // 認証
      certify: false,
      // エラーメッセージ
      errMsg: null,
      // 月リスト
      months: []
    }
  },
  created () {
    this.months = [...Array(12).keys()].map(i => ++i)
    if (this.newEntry) {
      this.loadFirst()
    } else {
      this.loadUser()
    }
  },
  computed: {
    /**
     * パスワードの検証
     */
    validPassword () {
      if (!this.forms.password || this.forms.password === this.forms.passwordConfirm) {
        return {
          error: false,
          message: null
        }
      } else {
        return {
          error: true,
          message: this.$t('password.confirm.error')
        }
      }
    },
    /**
     * 性別リスト
     */
    sexes () {
      return [
        {
          value: 1,
          text: this.$t('sex.man')
        },
        {
          value: 2,
          text: this.$t('sex.female')
        },
        {
          value: 3,
          text: this.$t('sex.other')
        }

      ]
    },
    /**
     * 誕生日（日）リスト
     */
    birthdayDays () {
      let max = 31
      let dt = this.utils.cast.date(this.forms.birthdayYear, this.forms.birthdayMonth + 1, 0)
      if (dt) {
        max = dt.getDate()
      }
      return [...Array(max).keys()].map(i => ++i)
    }
  },
  methods: {
    /**
     * ユーザー情報を読込
     */
    loadUser () {
      this.api.user({})
        .then(response => {
          this.forms.mail = response.data.mailAddress
          this.forms.name = response.data.customerName
          this.forms.address = response.data.address
          this.forms.tel = response.data.tel
          this.forms.sex = response.data.sex
          let birthday = this.utils.cast.date(response.data.birthday)
          if (birthday != null) {
            this.forms.birthdayYear = birthday.getFullYear()
            this.forms.birthdayMonth = birthday.getMonth() + 1
            this.forms.birthdayDay = birthday.getDate()
          }
          this.certify = true
        })
        .catch(response => {
          this.errMsg = this.$t(`error.${response.functionError.errorCode}`)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    /**
     * 新規登録時の情報を読込（HASHから登録情報を取得）
     */
    loadFirst () {
      this.api.userHashConfirm({
        userKind: 0,
        hash: this.$route.params.hash
      })
        .then(response => {
          this.forms.mail = response.data.mailAddress
          this.certify = true
        })
        .catch(response => {
          this.errMsg = this.$t(`error.${response.functionError.errorCode}`)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    /**
     * 次のSTEPに移動
     */
    nextStep (value) {
      this.step += value
      window.scrollTo(0, 0)
    },
    /**
     * 登録
     */
    regist () {
      if (this.isSubmit) {
        return
      }

      this.isSubmit = true
      this.errMsg = null

      this.api.userRegist({
        hash: this.$route.params.hash || '',
        mailAddress: this.forms.mail,
        customerName: this.forms.name,
        address: this.forms.address,
        password: this.forms.password,
        tel: this.forms.tel,
        sex: this.forms.sex,
        birthday: this.utils.cast.dateString(this.forms.birthdayYear, this.forms.birthdayMonth, this.forms.birthdayDay)
      })
        .then(response => {
          this.$router.push({name: 'EntryComplete', params: {newEntry: this.newEntry, kind: '0'}})
        })
        .catch(response => {
          this.errMsg = this.$t(`error.${response.functionError.errorCode}`)
        })
        .finally(() => {
          this.isSubmit = false
        })
    }
  }
}
</script>

<style lang="sass" scoped>
.tos-box
  overflow-y: scroll
  width: 100%
  height: 600px
  max-height: calc(100vh - 200px)
  border: solid 1px #888888
  background-color: #ffffff

.none-shadow
  box-shadow: none
</style>
